import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getUrl, TransferProgressEvent, uploadData } from 'aws-amplify/storage';
import { Maybe, Scalars } from '../../../gql/generated/queries';
import { ReportAttachmentListType } from '../../../types';
import { captureException } from '../../../vendor/sentry';
import { RootState } from '../../store';
import { ResizedUrlType, RotatedUrlType } from '@inspectify/core';

export type UploadedMedia = {
  id: string,
  fileName: string,
  updatedAt: string,
  progress: TransferProgressEvent,
  url?: string,
  error?: string,
  signedUrl?: string,
  mediaType?: string,
  metadata?: Maybe<Scalars['JsonAsString']['input']>,
  s3ObjectKey?: Maybe<Scalars['String']['output']>,
  contentType?: 'photo' | 'video' | 'pdf',
  resizedUrls?: Array<ResizedUrlType>;
  rotatedUrls?: Array<RotatedUrlType>;
  thumbnailUrl?: string,
}

const initialState = {
  uploading: false,
  uploads: {} as Record<string, UploadedMedia>,
};

export const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    addAttachment: (state, action: PayloadAction<ReportAttachmentListType>) => {
      state.uploads[action.payload.id] = {
        id: action.payload.id,
        fileName: action.payload.label || action.payload.fileName!,
        updatedAt: new Date().toISOString(),
        progress: {
          transferredBytes: 0,
          totalBytes: 0,
        },
        url: action.payload.attachmentUrl!,
        s3ObjectKey: action.payload.s3ObjectKey,
        mediaType: action.payload.mediaType!,
        resizedUrls: action.payload.resizedUrls!,
        rotatedUrls: action.payload.rotatedUrls!,
        thumbnailUrl: action.payload.thumbnailUrl!,
      };
    },
    removeAttachment: (state, action: PayloadAction<string>) => {
      delete state.uploads[action.payload];
    },
    addFiles: (state, action: PayloadAction<File[]>) => {
      action.payload.forEach((file) => {
        const id = crypto.randomUUID().toString();
        const contentType = file.type?.includes('video')
          ? 'video'
          : file.type?.includes('pdf')
            ? 'pdf'
            : 'photo';
        state.uploads[id] = {
          id,
          fileName: file.name.replaceAll(' ', '_'),
          url: URL.createObjectURL(file),
          updatedAt: new Date().toISOString(),
          progress: {
            transferredBytes: 0,
            totalBytes: 0,
          },
          contentType,
        };
      });
    },
    updateUpload: (state, action: PayloadAction<UploadedMedia>) => {
      state.uploads[action.payload.id] = action.payload;
    },
    removeUpload: (state, action: PayloadAction<string>) => {
      delete state.uploads[action.payload];
    },
    clearUploads: (state) => {
      state.uploads = {};
    },
    setUploading: (state, action: PayloadAction<boolean>) => {
      state.uploading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(uploadToS3.fulfilled, (state, action) => {
      if (!action.payload) return;
      // upload is done, remove it from the list
      delete state.uploads[action.payload.id];
    });

    builder.addCase(uploadToS3.rejected, (state, action) => {
      delete state.uploads[action.meta.arg.media.id];
    });
  },
});

export const uploadToS3 = createAsyncThunk(
  'upload/uploadToS3',
  async (item: { media: UploadedMedia, file: File }, { dispatch }) => {
    const authSession = await fetchAuthSession();
    try {
      dispatch(uploadActions.setUploading(true));
      const signedUrl = await getUrl({ path: item.media.fileName, options: { expiresIn: 60 } });
      const { path } = await uploadData({
        path: `private/${authSession.identityId}/${item.media.fileName}`,
        data: item.file,
        options: {
          onProgress: (progress) => {
            dispatch(uploadActions.updateUpload({ ...item.media, progress }));
          },
        }
      }).result;
      dispatch(uploadActions.setUploading(false));
      return { ...item.media, signedUrl: signedUrl.url.toString(), s3ObjectKey: path };
    } catch (e) {
      captureException(e, { source: 'uploadToS3' });
      item.media.error = (e as Error).message;
      return item.media;
    }
  },
);

export const uploadActions = uploadSlice.actions;

export default uploadSlice.reducer;

// selectors

export const selectUploads = (state: RootState) => state.uploads;
export const selectUpload = (state: RootState, id: string) => state.uploads.uploads[id];
export const selectUploadsArray = createSelector(
  selectUploads,
  (uploads) => Object.values(uploads.uploads),
);
export const selectRemainingCount = createSelector(
  selectUploadsArray,
  (uploads) => Object.values(uploads).length,
);
